import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, AlertCircle, User, Shield, ClipboardCheck } from 'lucide-react';
import { ThemeModeToggle } from '@/components/header/ThemeModeToggle';

/**
 * LoginForm Component
 * Login form for BE Study Automation System with role-based demo credentials
 */
export const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (error) setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Attempt login
    const result = login(formData.email, formData.password);

    if (result.success) {
      // Redirect to the appropriate dashboard based on role
      navigate(result.redirectTo);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Quick login helper
  const handleQuickLogin = (email) => {
    setFormData({ email, password: 'password123' });
  };

  return (
    <Card className="w-full max-w-md relative" data-testid="login-form">
      {/* Theme Toggle in top right */}
      <div className="absolute top-4 right-4">
        <ThemeModeToggle showTooltip={false} />
      </div>
      
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
            <FileText className="h-8 w-8 text-primary-foreground" />
          </div>
        </div>
        <CardTitle className="text-2xl font-bold text-center">BE Study System</CardTitle>
        <CardDescription className="text-center">
          Bioequivalence Study Automation Platform
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div
              className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
              data-testid="login-error"
            >
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              data-testid="login-email-input"
              autoComplete="email"
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              data-testid="login-password-input"
              autoComplete="current-password"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Demo Credentials */}
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground text-center mb-3">Quick Login (Demo)</p>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex flex-col items-center gap-1 h-auto py-2"
              onClick={() => handleQuickLogin('sarah.mitchell@biopharma.com')}
              data-testid="quick-login-admin"
            >
              <Shield className="h-4 w-4 text-red-500" />
              <span className="text-xs">Admin</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex flex-col items-center gap-1 h-auto py-2"
              onClick={() => handleQuickLogin('emily.chen@biopharma.com')}
              data-testid="quick-login-user"
            >
              <User className="h-4 w-4 text-blue-500" />
              <span className="text-xs">User</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex flex-col items-center gap-1 h-auto py-2"
              onClick={() => handleQuickLogin('james.wilson@biopharma.com')}
              data-testid="quick-login-reviewer"
            >
              <ClipboardCheck className="h-4 w-4 text-green-500" />
              <span className="text-xs">Reviewer</span>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Password: <code className="bg-muted px-1 rounded">password123</code>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
